/**
 * EcwShell — eCW Healow simulation.
 * - Today's appointment schedule with 2 eCW patients (clickable switcher)
 * - Full tab content per patient (SOAP, Vitals, History, Allergies, Meds, Orders, Labs, Coding Intel)
 * - Persistent right RAAPID panel (collapsible)
 * - All data from ecwDummy (no live FHIR for eCW — requires registration)
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MOCK_PROVIDERS, type MockProvider } from './data/mockProviders';
import {
  ROBERT, JAMES,
  ECW_VITALS, ECW_SOAP, ECW_ALLERGIES, ECW_HISTORY, ECW_MEDICATIONS, ECW_ORDERS, ECW_LABS, ECW_CHART_DOCS,
  JAMES_VITALS, JAMES_SOAP, JAMES_ALLERGIES, JAMES_HISTORY, JAMES_MEDICATIONS, JAMES_ORDERS, JAMES_LABS, JAMES_CHART_DOCS,
} from './data/ecwDummy';

// ─── Patient registry ────────────────────────────────────────────────────────

const ECW_PATIENTS = {
  'ECW-PAT-01293': {
    info: ROBERT,
    vitals: ECW_VITALS, soap: ECW_SOAP, allergies: ECW_ALLERGIES,
    history: ECW_HISTORY, meds: ECW_MEDICATIONS, orders: ECW_ORDERS, labs: ECW_LABS,
    chartDocs: ECW_CHART_DOCS,
    apptTime: '9:30 AM', status: 'In Room',
  },
  'ECW-PAT-02108': {
    info: JAMES,
    vitals: JAMES_VITALS, soap: JAMES_SOAP, allergies: JAMES_ALLERGIES,
    history: JAMES_HISTORY, meds: JAMES_MEDICATIONS, orders: JAMES_ORDERS, labs: JAMES_LABS,
    chartDocs: JAMES_CHART_DOCS,
    apptTime: '11:00 AM', status: 'Checked In',
  },
} as const;

type PatientId = keyof typeof ECW_PATIENTS;
const TABS = ['SOAP', 'Chart Review', 'Vitals', 'History', 'Allergies', 'Medications', 'Orders', 'Labs', 'RAAPID Coding Intel'];
const G = '#00897B'; const GD = '#00695C'; const GL = '#E8F5F3';

export default function EcwShell() {
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState<MockProvider | null>(null);
  const [patientId, setPatientId] = useState<PatientId>('ECW-PAT-01293');
  const [activeTab, setActiveTab] = useState('Chart Review');
  const [rightOpen, setRightOpen] = useState(true);

  const handleMessage = useCallback((e: MessageEvent) => {
    if (e.origin === window.location.origin && e.data?.raapid === 'openFull') setActiveTab('RAAPID Coding Intel');
  }, []);
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const filteredPatients = Object.entries(ECW_PATIENTS).filter(([, p]) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return p.info.name.toLowerCase().includes(q)
      || p.info.mrn.toLowerCase().includes(q)
      || p.info.encounter.toLowerCase().includes(q);
  });

  const searchOpen = searchQuery.trim().length > 0;

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const switchPatient = (id: PatientId) => { setPatientId(id); setScheduleOpen(false); setSearchQuery(''); };

  const pt = ECW_PATIENTS[patientId];
  const { info } = pt;
  const providerSpecialty = selectedProvider?.specialty ?? 'PCP';
  const providerNameParam = encodeURIComponent(selectedProvider?.name ?? '');
  const compactUrl = `${window.location.origin}${import.meta.env.BASE_URL}compact?mode=mock&theme=ecw&patientId=${patientId}&specialty=${providerSpecialty}&providerName=${providerNameParam}`;
  const panelUrl   = `${window.location.origin}${import.meta.env.BASE_URL}app?mode=mock&theme=ecw&patientId=${patientId}&specialty=${providerSpecialty}&providerName=${providerNameParam}`;

  if (!selectedProvider) {
    return <EcwProviderPicker onSelect={setSelectedProvider} onBack={() => navigate('/')} />;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '"Segoe UI", Arial, sans-serif', fontSize: '13px', background: '#F8F9FA', overflow: 'hidden' }}>

      {/* ── Top header ────────────────────────────────────────────────── */}
      <div style={{ background: 'white', borderBottom: '1px solid #E0E0E0', padding: '0 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '52px', flexShrink: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <button onClick={() => navigate('/')} style={ghost} title="Demo home"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: '10px', fontWeight: 900 }}>eCW</span>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '14px', color: '#1F2937' }}>eClinicalWorks <span style={{ color: G }}>Healow</span></div>
              <div style={{ fontSize: '10px', color: '#9CA3AF' }}>{info.practice}</div>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
          <div style={{ background: '#F3F4F6', border: `1px solid ${searchOpen ? '#00897B' : '#E5E7EB'}`, borderRadius: '20px', padding: '5px 14px', display: 'flex', alignItems: 'center', gap: '6px', width: '220px', transition: 'border-color 0.15s' }}>
            <span style={{ color: '#9CA3AF', fontSize: '12px' }}>🔍</span>
            <input
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search patient..."
              style={{ background: 'transparent', border: 'none', outline: 'none', color: '#374151', fontSize: '12px', width: '100%' }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '14px', padding: 0, lineHeight: 1 }}>&times;</button>
            )}
          </div>
          {searchOpen && (
            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100, background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', boxShadow: '0 12px 32px rgba(0,0,0,0.15)', width: '340px', maxHeight: '290px', overflowY: 'auto' }}>
              <div style={{ padding: '6px 12px', background: '#F9FAFB', fontSize: '10px', color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #F3F4F6', position: 'sticky', top: 0 }}>
                Search Results · {filteredPatients.length} found
              </div>
              {filteredPatients.length === 0 ? (
                <div style={{ padding: '20px 14px', textAlign: 'center' }}>
                  <div style={{ color: '#6B7280', fontSize: '12px' }}>No patients match "<span style={{ color: '#111827' }}>{searchQuery}</span>"</div>
                  <div style={{ color: '#9CA3AF', fontSize: '11px', marginTop: '4px' }}>Try searching by name, MRN, or encounter type</div>
                </div>
              ) : (
                filteredPatients.map(([id, p]) => {
                  const active = id === patientId;
                  return (
                    <button key={id} onClick={() => switchPatient(id as PatientId)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '10px', padding: '10px 14px', background: active ? '#E0F2F1' : 'transparent', border: 'none', borderBottom: '1px solid #F3F4F6', cursor: 'pointer', textAlign: 'left' }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#F9FAFB'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: active ? '#00897B' : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? 'white' : '#9CA3AF', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{p.info.initials}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#111827', fontSize: '12px', fontWeight: 700 }}>{p.info.name}</div>
                        <div style={{ color: '#6B7280', fontSize: '10px', marginTop: '1px' }}>
                          MRN: {p.info.mrn} · {p.info.age} · {p.info.encounter}
                        </div>
                      </div>
                      <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, background: p.status === 'In Room' ? '#DCFCE7' : '#FEF3C7', color: p.status === 'In Room' ? '#15803D' : '#B45309', flexShrink: 0 }}>{p.status}</span>
                    </button>
                  );
                })
              )}
            </div>
          )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }} style={{ background: notifOpen ? '#F3F4F6' : 'transparent', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '14px', padding: '4px 6px', borderRadius: '4px', position: 'relative' }}>
              🔔
              <span style={{ position: 'absolute', top: '0', right: '2px', width: '14px', height: '14px', borderRadius: '50%', background: '#EF4444', color: 'white', fontSize: '8px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
            </button>
            {notifOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100, background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', boxShadow: '0 12px 32px rgba(0,0,0,0.15)', width: '320px', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', background: '#F9FAFB', fontSize: '11px', color: '#374151', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F3F4F6' }}>
                  <span>Notifications</span>
                  <span style={{ fontSize: '10px', color: G, cursor: 'pointer' }}>Mark all read</span>
                </div>
                {[
                  { icon: '🎯', text: 'RAAPID: 2 new HCC coding gaps detected for Garcia, Robert J.', time: '10 min ago', unread: true },
                  { icon: '⚠️', text: 'Suspecting Emerging: condition G47.33 — documented but never coded for Garcia, R.', time: '25 min ago', unread: true },
                  { icon: '✅', text: 'Code E11.9 accepted and written back to EHR successfully', time: '1 hr ago', unread: true },
                  { icon: '📊', text: 'RAF score updated: +0.549 projected lift for Anderson, James R.', time: '2 hrs ago', unread: false },
                ].map((n, i) => (
                  <div key={i} style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: '10px', alignItems: 'flex-start', background: n.unread ? `${G}08` : 'transparent', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = n.unread ? `${G}08` : 'transparent'}
                  >
                    <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>{n.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', color: '#374151', lineHeight: 1.4 }}>{n.text}</div>
                      <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '2px' }}>{n.time}</div>
                    </div>
                    {n.unread && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: G, flexShrink: 0, marginTop: '4px' }} />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Profile */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: profileOpen ? '#F3F4F6' : 'transparent', border: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: '6px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 700 }}>{selectedProvider.initials}</div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{selectedProvider.name}</div>
                <div style={{ fontSize: '10px', color: '#9CA3AF' }}>{selectedProvider.title}</div>
              </div>
              <span style={{ color: '#D1D5DB' }}>{profileOpen ? '▲' : '▼'}</span>
            </button>
            {profileOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100, background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', boxShadow: '0 12px 32px rgba(0,0,0,0.15)', width: '220px', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: 700 }}>{selectedProvider.initials}</div>
                  <div>
                    <div style={{ color: '#111827', fontSize: '12px', fontWeight: 700 }}>{selectedProvider.name}</div>
                    <div style={{ color: '#9CA3AF', fontSize: '10px' }}>{selectedProvider.title}</div>
                  </div>
                </div>
                {[
                  { label: 'My Profile', icon: '👤' },
                  { label: 'Preferences', icon: '⚙️' },
                  { label: 'Help & Support', icon: '❓' },
                ].map((item) => (
                  <div key={item.label} style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#374151', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '13px' }}>{item.icon}</span>{item.label}
                  </div>
                ))}
                <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#EF4444', fontSize: '12px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setSelectedProvider(null)}
                >
                  <span style={{ fontSize: '13px' }}>🚪</span>Sign Out
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Today's Appointments dropdown ─────────────────────────────── */}
      <div style={{ background: '#F0FAF9', borderBottom: `1px solid ${G}20`, padding: '5px 14px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, position: 'relative' }}>
        <span style={{ color: '#9CA3AF', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>Schedule</span>
        <button onClick={() => setScheduleOpen(!scheduleOpen)} style={{ background: 'white', border: `1px solid ${scheduleOpen ? G : '#E5E7EB'}`, borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '420px', boxShadow: scheduleOpen ? `0 0 0 2px ${G}30` : 'none' }}>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <div style={{ color: '#111827', fontSize: '12px', fontWeight: 700 }}>{info.name}</div>
            <div style={{ color: '#9CA3AF', fontSize: '10px' }}>{info.encounter} · {pt.apptTime}</div>
          </div>
          <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, background: pt.status === 'In Room' ? '#DCFCE7' : '#FEF3C7', color: pt.status === 'In Room' ? '#15803D' : '#B45309', flexShrink: 0 }}>{pt.status}</span>
          <span style={{ color: '#9CA3AF', fontSize: '10px' }}>{scheduleOpen ? '▲' : '▼'}</span>
        </button>
        {/* Dropdown */}
        {scheduleOpen && (
          <div style={{ position: 'absolute', top: '100%', left: '14px', zIndex: 50, background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '400px' }}>
            <div style={{ padding: '8px 14px', background: GL, borderBottom: `1px solid ${G}20`, fontSize: '10px', color: GD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Today's Appointments</div>
            {Object.entries(ECW_PATIENTS).map(([id, p]) => {
              const active = id === patientId;
              return (
                <button key={id} onClick={() => switchPatient(id as PatientId)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '12px', padding: '10px 14px', background: active ? GL : 'transparent', border: 'none', borderBottom: '1px solid #F3F4F6', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#F9FAFB'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: active ? G : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? 'white' : '#9CA3AF', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>{p.info.initials}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#111827', fontSize: '13px', fontWeight: 700 }}>{p.info.name}</div>
                    <div style={{ color: '#6B7280', fontSize: '11px', marginTop: '2px' }}>{p.info.age} · {p.info.encounter} · MRN: {p.info.mrn}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ color: '#374151', fontSize: '12px', fontWeight: 600 }}>{p.apptTime}</div>
                    <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, background: p.status === 'In Room' ? '#DCFCE7' : '#FEF3C7', color: p.status === 'In Room' ? '#15803D' : '#B45309' }}>{p.status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Patient bar ───────────────────────────────────────────────── */}
      <div style={{ background: GL, borderBottom: `2px solid ${G}`, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '15px', flexShrink: 0 }}>{info.initials}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>{info.name}</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{info.gender} · {info.age} · DOB: {info.dob} · MRN: {info.mrn}</div>
          </div>
          <EDiv /><div>
            <div style={{ fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Encounter</div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{info.encounter} · {info.date}</div>
          </div>
          <EDiv /><div>
            <div style={{ fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Insurance</div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{info.plan}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ background: G, color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700 }}>Medicare Advantage</span>
        </div>
      </div>

      {/* ── Encounter tabs ────────────────────────────────────────────── */}
      <div style={{ background: 'white', borderBottom: '1px solid #E5E7EB', padding: '0 14px', display: 'flex', flexShrink: 0, overflowX: 'auto' }}>
        {TABS.map((tab) => {
          const active = activeTab === tab;
          const isCodingIntel = tab === 'RAAPID Coding Intel';
          return (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '10px 14px', border: 'none', borderBottom: active ? `2px solid ${G}` : '2px solid transparent', background: 'transparent', color: active ? G : '#6B7280', fontWeight: active ? 700 : 400, fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '5px', fontFamily: '"Segoe UI", Arial, sans-serif' }}>
              {tab}
            </button>
          );
        })}
      </div>

      {/* ── Content + right panel ─────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        {/* Tab content — key forces remount on patient switch */}
        <div style={{ flex: 1, overflow: 'auto' }} key={patientId}>
          {activeTab === 'RAAPID Coding Intel' ? (
            <iframe src={panelUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="RAAPID Coding Intelligence" allow="clipboard-write" />
          ) : activeTab === 'SOAP' ? <SoapTab pt={pt} />
            : activeTab === 'Chart Review' ? <ChartReviewTab docs={pt.chartDocs} />
            : activeTab === 'Vitals' ? <VitalsTab vitals={pt.vitals} />
            : activeTab === 'History' ? <HistoryTab history={pt.history} />
            : activeTab === 'Allergies' ? <AllergiesTab allergies={pt.allergies} />
            : activeTab === 'Medications' ? <MedsTab meds={pt.meds} />
            : activeTab === 'Orders' ? <OrdersTab orders={pt.orders} />
            : activeTab === 'Labs' ? <LabsTab labs={pt.labs} />
            : null}
        </div>

        {/* Right RAAPID panel */}
        <div style={{ width: rightOpen ? '360px' : '32px', flexShrink: 0, borderLeft: `2px solid ${G}30`, display: 'flex', flexDirection: 'column', background: 'white', transition: 'width 0.2s ease', overflow: 'hidden' }}>
          <div style={{ background: G, padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            {rightOpen && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <img src={`${import.meta.env.BASE_URL}raapid-logo.png`} alt="RAAPID" style={{ height: '14px', filter: 'brightness(0) invert(1)' }} />
                <span style={{ color: 'white', fontSize: '11px', fontWeight: 600, opacity: 0.9 }}>Coding Intelligence</span>
              </div>
            )}
            <button onClick={() => setRightOpen(!rightOpen)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '12px', marginLeft: rightOpen ? 0 : 'auto' }}>{rightOpen ? '→' : '←'}</button>
          </div>
          {rightOpen && <iframe key={patientId} src={compactUrl} style={{ flex: 1, height: 0, minHeight: 0, border: 'none', width: '100%', background: '#F9FAFB' }} title="RAAPID Summary" allow="clipboard-write" />}
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: '#F9FAFB', borderTop: '1px solid #E5E7EB', padding: '3px 14px', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ color: '#D1D5DB', fontSize: '10px' }}>eClinicalWorks Healow™ · Demo · v12.0.8</span>
        <span style={{ color: '#D1D5DB', fontSize: '10px' }}>{info.practice} · Session active</span>
      </div>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

function SoapTab({ pt }: { pt: typeof ECW_PATIENTS[PatientId] }) {
  const s = pt.soap;
  return (
    <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <SSection letter="S" color="#1D4ED8" title="Subjective" text={s.subjective} />
      <SSection letter="O" color="#059669" title="Objective" text={s.objective} />
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ background: '#FEF3C7', padding: '8px 14px', borderBottom: '1px solid #FDE68A', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ background: '#B45309', color: 'white', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800 }}>A</span>
          <span style={{ fontWeight: 700, fontSize: '13px', color: '#92400E' }}>Assessment</span>
        </div>
        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {s.assessment.map((dx) => (
            <div key={dx.icd} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px' }}>
              <span style={{ fontSize: '12px', color: '#111827' }}>{dx.desc}</span>
              <code style={{ fontSize: '11px', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>{dx.icd}</code>
            </div>
          ))}
          <div style={{ fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '4px' }}>⚠ RAAPID has flagged additional care gaps — see Coding Intel tab</div>
        </div>
      </div>
      <SSection letter="P" color="#7C3AED" title="Plan" text={s.plan} />
    </div>
  );
}

function SSection({ letter, color, title, text }: { letter: string; color: string; title: string; text: string }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ background: `${color}10`, padding: '8px 14px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ background: color, color: 'white', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800 }}>{letter}</span>
        <span style={{ fontWeight: 700, fontSize: '13px', color }}>{title}</span>
      </div>
      <div style={{ padding: '10px 14px', fontSize: '12px', color: '#374151', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  );
}

function VitalsTab({ vitals }: { vitals: typeof ECW_VITALS }) {
  const v = vitals;
  const isObj = !Array.isArray(v);
  const single = isObj ? v : (v as typeof ECW_VITALS[])[0];
  const rows = [
    { label: 'Blood Pressure', value: single.bp, alert: parseInt(single.bp) > 140 },
    { label: 'Heart Rate', value: single.hr, alert: single.hr.includes('irreg') },
    { label: 'Respiratory Rate', value: `${single.rr} /min` },
    { label: 'SpO₂', value: single.spo2, alert: parseInt(single.spo2) < 95 },
    { label: 'Temperature', value: single.temp },
    { label: 'Weight / BMI', value: `${single.wt} / BMI ${single.bmi}` },
    { label: 'Pain Score', value: single.pain },
  ];
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', maxWidth: '540px' }}>
        <div style={{ background: GL, padding: '8px 14px', borderBottom: `1px solid ${G}30`, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: '13px', color: GD }}>Vital Signs</span>
          <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{single.date}</span>
        </div>
        {rows.map((r) => (
          <div key={r.label} style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#6B7280' }}>{r.label}</span>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 700, fontSize: '13px', color: r.alert ? '#B45309' : '#111827' }}>{r.value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryTab({ history }: { history: typeof ECW_HISTORY }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', alignContent: 'start' }}>
      {[['Past Medical History', history.pmh, G], ['Surgical History', history.surgical, '#6D28D9'], ['Social History', history.social, '#1D4ED8'], ['Family History', history.family, '#B45309']].map(([title, items, color]) => (
        <div key={title as string} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: `${color}10`, padding: '8px 12px', borderBottom: '1px solid #E5E7EB' }}>
            <span style={{ fontWeight: 700, fontSize: '12px', color: color as string }}>{title as string}</span>
          </div>
          <ul style={{ padding: '8px 12px 8px 24px', margin: 0 }}>
            {(items as string[]).map((item, i) => <li key={i} style={{ fontSize: '12px', color: '#374151', marginBottom: '5px', lineHeight: 1.4 }}>{item}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}

function AllergiesTab({ allergies }: { allergies: typeof ECW_ALLERGIES }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', maxWidth: '700px' }}>
        <div style={{ background: '#FEF2F2', padding: '8px 14px', borderBottom: '1px solid #FECACA' }}>
          <span style={{ fontWeight: 700, fontSize: '13px', color: '#991B1B' }}>⚠ Allergies / Adverse Reactions</span>
        </div>
        {allergies.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>No known drug allergies (NKDA)</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead><tr style={{ background: '#F9FAFB' }}>{['Allergen','Reaction','Severity','Onset'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '1px solid #E5E7EB', fontSize: '11px' }}>{h}</th>)}</tr></thead>
            <tbody>{allergies.map((a, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '10px 12px', fontWeight: 700, color: '#991B1B' }}>{a.allergen}</td>
                <td style={{ padding: '10px 12px', color: '#374151' }}>{a.reaction}</td>
                <td style={{ padding: '10px 12px' }}><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, background: a.severity==='Moderate'?'#FEF3C7':'#F3F4F6', color: a.severity==='Moderate'?'#B45309':'#6B7280' }}>{a.severity}</span></td>
                <td style={{ padding: '10px 12px', color: '#9CA3AF' }}>{a.onset}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MedsTab({ meds }: { meds: typeof ECW_MEDICATIONS }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ background: GL, padding: '8px 14px', borderBottom: `1px solid ${G}30` }}>
          <span style={{ fontWeight: 700, fontSize: '13px', color: GD }}>Active Medications ({meds.length})</span>
        </div>
        {meds.map((m, i) => (
          <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', background: i%2?'#FAFAFA':'white' }}>
            <div style={{ fontWeight: 700, fontSize: '12px', color: '#111827' }}>{m.name}</div>
            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{m.sig}</div>
            <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '2px' }}>Prescribed: {m.date}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab({ orders }: { orders: typeof ECW_ORDERS }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ background: GL, padding: '8px 14px', borderBottom: `1px solid ${G}30` }}>
          <span style={{ fontWeight: 700, fontSize: '13px', color: GD }}>Encounter Orders</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead><tr style={{ background: '#F9FAFB' }}>{['Order','Type','Priority','Status','Date'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '1px solid #E5E7EB', fontSize: '11px' }}>{h}</th>)}</tr></thead>
          <tbody>{orders.map((o, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #F3F4F6', background: i%2?'#FAFAFA':'white' }}>
              <td style={{ padding: '9px 12px', fontWeight: 600, color: '#111827' }}>{o.name}</td>
              <td style={{ padding: '9px 12px', color: '#6B7280' }}>{o.type}</td>
              <td style={{ padding: '9px 12px' }}><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, background: o.priority==='STAT'?'#FEE2E2':o.priority==='Urgent'?'#FEF3C7':'#F3F4F6', color: o.priority==='STAT'?'#991B1B':o.priority==='Urgent'?'#B45309':'#6B7280' }}>{o.priority}</span></td>
              <td style={{ padding: '9px 12px' }}><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, background: o.status==='Ordered'?'#EFF6FF':o.status==='Scheduled'?'#F5F3FF':'#FFFBEB', color: o.status==='Ordered'?'#1D4ED8':o.status==='Scheduled'?'#6D28D9':'#B45309' }}>{o.status}</span></td>
              <td style={{ padding: '9px 12px', color: '#9CA3AF', fontSize: '11px' }}>{o.date}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function LabsTab({ labs }: { labs: typeof ECW_LABS }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ background: GL, padding: '8px 14px', borderBottom: `1px solid ${G}30` }}>
          <span style={{ fontWeight: 700, fontSize: '13px', color: GD }}>Lab Results</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead><tr style={{ background: '#F9FAFB' }}>{['Test','Result','Reference','Flag','Date','Note'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '1px solid #E5E7EB', fontSize: '11px' }}>{h}</th>)}</tr></thead>
          <tbody>{labs.map((lab, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #F3F4F6', background: lab.flag?'#FFFBEB':i%2?'#FAFAFA':'white' }}>
              <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{lab.name}</td>
              <td style={{ padding: '8px 10px', fontWeight: 700, color: lab.flag==='H'?'#B45309':lab.flag==='L'?'#1D4ED8':'#111827' }}>{lab.value}</td>
              <td style={{ padding: '8px 10px', color: '#9CA3AF', fontSize: '11px' }}>{lab.ref}</td>
              <td style={{ padding: '8px 10px' }}>{lab.flag && <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 700, background: lab.flag==='H'?'#FEF3C7':'#DBEAFE', color: lab.flag==='H'?'#B45309':'#1D4ED8' }}>{lab.flag}</span>}</td>
              <td style={{ padding: '8px 10px', color: '#6B7280', fontFamily: 'monospace', fontSize: '11px' }}>{lab.date}</td>
              <td style={{ padding: '8px 10px', color: lab.note?'#B45309':'#9CA3AF', fontSize: '11px', fontStyle: 'italic' }}>{lab.note}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

const ECW_DOC_STYLE: Record<string, { bg: string; color: string }> = {
  Lab:       { bg: '#EFF6FF', color: '#1D4ED8' },
  Imaging:   { bg: '#F0FDF4', color: '#15803D' },
  Consult:   { bg: '#FFF7ED', color: '#C2410C' },
  Procedure: { bg: '#FAF5FF', color: '#7E22CE' },
  ED:        { bg: '#FEF2F2', color: '#DC2626' },
  Note:      { bg: '#F0F4F8', color: '#374151' },
};

function ChartReviewTab({ docs }: { docs: readonly { date: string; type: string; category: string; provider: string; excerpt: string }[] }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selected = docs[selectedIdx];
  const cat = selected ? (ECW_DOC_STYLE[selected.category] || ECW_DOC_STYLE.Lab) : ECW_DOC_STYLE.Lab;
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '14px', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <h3 style={{ fontSize: '13px', fontWeight: 700, color: GD, margin: 0 }}>Chart Review</h3>
        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{docs.length} documents</span>
      </div>
      <select
        value={selectedIdx}
        onChange={e => setSelectedIdx(Number(e.target.value))}
        style={{ width: '100%', padding: '8px 10px', fontSize: '12px', border: '1px solid #E5E7EB', borderRadius: '6px', background: 'white', color: GD, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
      >
        {docs.map((d, i) => (
          <option key={i} value={i}>[{d.category}] {d.date} — {d.type}</option>
        ))}
      </select>
      {selected && (
        <div style={{ flex: 1, overflow: 'auto', background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
          <div style={{ background: GL, padding: '10px 14px', borderBottom: `2px solid ${G}40`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '8px', fontWeight: 700, background: cat.bg, color: cat.color }}>{selected.category}</span>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: GD }}>{selected.type}</div>
                <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{selected.provider}</div>
              </div>
            </div>
            <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'monospace' }}>{selected.date}</span>
          </div>
          <div style={{ padding: '14px', fontSize: '12px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: "'Courier New', Courier, monospace" }}>{selected.excerpt}</div>
        </div>
      )}
    </div>
  );
}

function EDiv() { return <div style={{ width: '1px', height: '32px', background: '#D1FAF5', flexShrink: 0 }} />; }
const ghost: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', color: '#6B7280', fontSize: '16px' };

function EcwProviderPicker({ onSelect, onBack }: { onSelect: (p: MockProvider) => void; onBack: () => void }) {
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '"Segoe UI", Arial, sans-serif', background: '#F8F9FA' }}>
      <div style={{ background: 'white', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: `3px solid ${G}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Back to home"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'white', fontSize: '12px', fontWeight: 900 }}>e</span>
          </div>
          <span style={{ color: '#111827', fontWeight: 700, fontSize: '15px' }}>eClinicalWorks</span>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span style={{ color: 'white', fontSize: '22px', fontWeight: 900 }}>e</span>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>Select Provider</h2>
            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>Choose your provider profile to begin this session</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {MOCK_PROVIDERS.map((p) => (
              <button
                key={p.initials}
                onClick={() => onSelect(p)}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = G; e.currentTarget.style.boxShadow = `0 0 0 3px ${G}20`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>{p.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{p.name}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{p.title}</div>
                </div>
                <span style={{ color: '#D1D5DB', fontSize: '18px' }}>→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
